import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SignUpDialog from "@/components/SignUpDialog";
import { useAuth } from "@/hooks/useAuth";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import heroImg from "@/assets/hero-community-hands.jpg";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getBlock } from "@/lib/siteContent";
import { useSeason } from "@/components/seasonal/SeasonalThemeProvider";

const Hero = () => {
  const [signUpOpen, setSignUpOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { needsWelcome, hasIdea, hasCompanyProfile, progress, loading } =
    useOnboardingStatus();
  const { blocks } = useSiteContent("home");
  const { season } = useSeason();
  const eyebrowLeft = getBlock(blocks, "hero_eyebrow_left", "Alabama");
  const eyebrowRight = getBlock(blocks, "hero_eyebrow_right", "Civic Infrastructure");
  const headline = getBlock(blocks, "hero_headline", "Powering the work that moves communities forward.");
  const subheadline = getBlock(
    blocks,
    "hero_subheadline",
    "A government affairs firm built for small, community-rooted Alabama organizations.",
  );

  const ctaLabel = !user
    ? "Get Started"
    : needsWelcome
      ? "Continue Setup"
      : !hasIdea
        ? "Post an Idea"
        : "Go to Profile";

  const handleCta = () => {
    if (!user) return setSignUpOpen(true);
    if (loading) return;
    if (needsWelcome) return navigate("/welcome");
    if (!hasIdea) return navigate("/ideas?new=1");
    return navigate("/profile");
  };

  return (
    <>
    <SignUpDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
    <section className="relative bg-hero overflow-hidden pt-20">
      {/* Hero photo backdrop on right half */}
      <div className="absolute inset-y-0 right-0 w-full md:w-3/5 lg:w-1/2">
        <img
          src={heroImg}
          alt="Two pairs of hands clasped together over an Alabama map"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(140_12%_14%)] via-[hsl(140_12%_14%)]/60 to-transparent md:via-transparent md:from-[hsl(140_12%_14%)]/90" />
      </div>

      <div className="container mx-auto px-6 pt-12 md:pt-20 pb-0 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-start min-h-[70vh]">
          <div className="max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-6 text-hero-muted text-xs font-semibold tracking-[0.2em] uppercase"
            >
              <span>{eyebrowLeft}</span>
              <span className="w-1 h-1 rounded-full bg-current opacity-60" />
              <span>{eyebrowRight}</span>
            </motion.div>

            {season && season.badge_text && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide border border-white/15 bg-white/5 backdrop-blur text-hero-fg"
              >
                {season.badge_emoji && <span aria-hidden>{season.badge_emoji}</span>}
                <span>{season.badge_text}</span>
              </motion.div>
            )}

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-hero-fg mb-5 md:mb-6"
            >
              {headline}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-hero-muted text-sm md:text-lg max-w-md mb-8 md:mb-10 leading-relaxed"
            >
              {subheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mb-20"
            >
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-display font-semibold text-base px-7 h-12 rounded-full shadow-glow"
                onClick={handleCta}
              >
                {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          </div>

          {/* Spacer for photo column */}
          <div className="hidden md:block" aria-hidden />
        </div>

      </div>
    </section>
    </>);

};

export default Hero;